import React, { useState, useCallback, useEffect } from 'react';
import { FIREBASE_AUTH, FIREBASE_DB } from '../firebase.config';
import { collection, query, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { RefreshCw, Eye, CheckCircle, XCircle } from 'lucide-react';

const AdminResignations = () => {
  const [resignations, setResignations] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchResignations = useCallback(async () => {
    try {
      setLoading(true);
      const resignationsQuery = query(collection(FIREBASE_DB, 'resignations'));
      const querySnapshot = await getDocs(resignationsQuery);
      const resignationsList = [];
      
      for (const docSnapshot of querySnapshot.docs) {
        const resignationData = docSnapshot.data();
        // Get employee details
        const employeeDocRef = doc(FIREBASE_DB, 'users', resignationData.employeeId);
        const employeeDocSnapshot = await getDoc(employeeDocRef);
        const employeeData = employeeDocSnapshot.exists() ? employeeDocSnapshot.data() : { name: 'Unknown Employee' };
        
        resignationsList.push({
          id: docSnapshot.id,
          ...resignationData,
          employeeName: employeeData.name || 'Unknown Employee',
          employeeEmail: employeeData.email || 'No email'
        });
      }
      
      setResignations(resignationsList.sort((a, b) => new Date(b.submissionDate) - new Date(a.submissionDate)));
    } catch (error) {
      console.error('Error fetching resignations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchResignations();
  }, [fetchResignations]);

  const updateResignationStatus = async (resignationId, newStatus) => {
    try {
      const docRef = doc(FIREBASE_DB, 'resignations', resignationId);
      await updateDoc(docRef, {
        status: newStatus,
        reviewedAt: new Date().toISOString(),
        reviewedBy: FIREBASE_AUTH.currentUser.uid
      });
      await fetchResignations();
    } catch (error) {
      console.error('Error updating resignation status:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">
              Employee Resignations
            </h1>
            <button
              onClick={fetchResignations}
              className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Employee
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Working Day
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Reason
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Submission Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {resignations.map((resignation) => (
                    <tr key={resignation.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {resignation.employeeName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {resignation.employeeEmail}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {new Date(resignation.lastWorkingDay).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {resignation.reason}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {new Date(resignation.submissionDate).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full 
                          ${(resignation.status || 'pending') === 'approved' ? 'bg-green-100 text-green-800' : 
                            (resignation.status || 'pending') === 'rejected' ? 'bg-red-100 text-red-800' : 
                            'bg-yellow-100 text-yellow-800'}`}>
                          {((resignation.status || 'pending').charAt(0).toUpperCase() + (resignation.status || 'pending').slice(1))}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => window.open(resignation.letterUrl, '_blank')}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Resignation Letter"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                          {(!resignation.status || resignation.status === 'pending') && (
                            <>
                              <button
                                onClick={() => updateResignationStatus(resignation.id, 'approved')}
                                className="text-green-600 hover:text-green-900"
                                title="Accept Resignation"
                              >
                                <CheckCircle className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => updateResignationStatus(resignation.id, 'rejected')}
                                className="text-red-600 hover:text-red-900"
                                title="Reject Resignation"
                              >
                                <XCircle className="w-5 h-5" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {resignations.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  No resignation requests submitted yet
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminResignations; 